import { jsPDF } from 'jspdf';
import { GrowthTrajectoryDto } from '../../../models/analytics/dto/get-growth-trajectory.dto';
import { ImpactSummaryDto } from '../../../models/analytics/dto/get-impact-summary.dto';
import { RevenuePerPlayerDto } from '../../../models/analytics/dto/get-revenue-per-player.dto';

const BRAND_COLOR: [number, number, number] = [2, 63, 127]; // brand.500
const GRAY_700: [number, number, number] = [55, 65, 81];
const GRAY_500: [number, number, number] = [107, 114, 128];
const SUCCESS: [number, number, number] = [16, 185, 129];

function fmt(n: number): string {
    // Strip non-breaking / thin spaces that jsPDF cannot render
    return Math.round(n).toLocaleString('fr-FR').replace(/\s/g, ' ');
}

export function exportImpactPdf({
    organizationName,
    locationName,
    startDate,
    endDate,
    summary,
    topPlayers,
    growth,
}: {
    organizationName: string;
    locationName: string;
    startDate: Date;
    endDate: Date;
    summary: ImpactSummaryDto;
    topPlayers: RevenuePerPlayerDto;
    growth: GrowthTrajectoryDto;
}): void {
    const doc = new jsPDF({ unit: 'mm', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 20;
    const contentW = pageW - margin * 2;
    let y = margin;

    // Header
    doc.setFillColor(...BRAND_COLOR);
    doc.rect(0, 0, pageW, 40, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.setFont('helvetica', 'bold');
    doc.text('MySportSet — Impact Report', margin, 26);
    y = 50;

    // Subtitle: org / location / range
    doc.setTextColor(...GRAY_500);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(
        `${organizationName} — ${locationName} — ${startDate.toLocaleDateString('fr-FR')} → ${endDate.toLocaleDateString('fr-FR')}`,
        margin,
        y,
    );
    y += 12;

    // Hero: "Revenue you wouldn't have had"
    doc.setFillColor(...BRAND_COLOR);
    doc.roundedRect(margin, y - 5, contentW, 30, 3, 3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.text('Revenue you wouldn\'t have had without MySportSet', margin + 8, y + 4);
    doc.setFontSize(28);
    doc.setFont('helvetica', 'bold');
    doc.text(`${fmt(summary.recoveredRevenue)} €`, margin + 8, y + 18);
    y += 38;

    // Key metrics grid
    doc.setTextColor(...GRAY_700);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Key Metrics', margin, y);
    y += 8;

    const metrics: Array<[string, string]> = [
        ['Total Bookings', fmt(summary.totalBookings)],
        ['Self-Service Bookings', fmt(summary.selfServiceBookings)],
        ['Spots Recovered', fmt(summary.replacementsFulfilled)],
        ['New Players', fmt(summary.newPlayers)],
        ['Total Revenue', `${fmt(summary.totalRevenue)} €`],
        ['Recovered Revenue', `${fmt(summary.recoveredRevenue)} €`],
    ];

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const colW = contentW / 3;
    metrics.forEach((row, idx) => {
        const col = idx % 3;
        const rowIdx = Math.floor(idx / 3);
        const x = margin + col * colW;
        const rowY = y + rowIdx * 18;

        doc.setTextColor(...GRAY_500);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        doc.text(row[0], x, rowY);

        doc.setTextColor(...GRAY_700);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text(row[1], x, rowY + 7);
    });
    y += Math.ceil(metrics.length / 3) * 18 + 8;

    // Top players
    if (topPlayers.length > 0) {
        doc.setTextColor(...GRAY_700);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Top Players by Revenue', margin, y);
        y += 8;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        topPlayers.slice(0, 10).forEach((item, idx) => {
            if (y > pageH - margin - 10) {
                doc.addPage();
                y = margin;
            }
            doc.setTextColor(...GRAY_500);
            doc.text(`${idx + 1}.`, margin, y);
            doc.setTextColor(...GRAY_700);
            doc.text(
                `${item.user.firstName} ${item.user.lastInitial}`,
                margin + 8,
                y,
            );
            doc.setTextColor(...SUCCESS);
            doc.setFont('helvetica', 'bold');
            doc.text(
                `${fmt(item.totalRevenue)} €`,
                pageW - margin,
                y,
                { align: 'right' },
            );
            doc.setFont('helvetica', 'normal');
            doc.setTextColor(...GRAY_500);
            doc.text(
                `${item.bookingCount} bookings`,
                pageW - margin - 30,
                y,
                { align: 'right' },
            );
            y += 7;
        });
        y += 4;
    }

    // Growth summary
    if (growth.length > 0) {
        if (y > pageH - margin - 40) {
            doc.addPage();
            y = margin;
        }
        doc.setTextColor(...GRAY_700);
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('Growth Trajectory', margin, y);
        y += 8;

        const totalBookings = growth.reduce((sum, p) => sum + p.bookings, 0);
        const totalRevenue = growth.reduce((sum, p) => sum + p.revenue, 0);
        const totalPlayers = growth.reduce((sum, p) => sum + p.newPlayers, 0);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...GRAY_500);
        doc.text(
            `Since venue creation — ${growth.length} periods tracked`,
            margin,
            y,
        );
        y += 6;

        doc.setTextColor(...GRAY_700);
        doc.text(`Total bookings: ${fmt(totalBookings)}`, margin, y);
        y += 5;
        doc.text(`Total revenue: ${fmt(totalRevenue)} €`, margin, y);
        y += 5;
        doc.text(`Total new players: ${fmt(totalPlayers)}`, margin, y);
        y += 8;
    }

    // Footer
    const footerY = pageH - 12;
    doc.setTextColor(...GRAY_500);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'italic');
    doc.text(
        `Generated on ${new Date().toLocaleDateString('fr-FR')} by MySportSet`,
        pageW / 2,
        footerY,
        { align: 'center' },
    );

    const filename = `impact-${organizationName.toLowerCase().replace(/\s+/g, '-')}-${startDate.toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
}
